<template>
    <main v-if="data && data.page">
        <article>
            <div class="heroCont">
                <div class="infoBar">
                    <WebsiteBreadcrumbs>
                        <a>publications</a>
                        <NuxtLink to="/publications/bookshop">bookshop</NuxtLink>
                    </WebsiteBreadcrumbs>
                    <div class="date baron headerText" v-if="articleDate">
                        <span>{{ articleDate.getDate().toString().padStart(2, '0') }}</span>
                        <span>{{ (articleDate.getMonth() + 1).toString().padStart(2, '0') }}</span>
                        <span>{{ articleDate.getFullYear() }}</span>
                    </div>
                </div>
                <h1 class="helvetica title1">{{ data.page?.title }}</h1>
            </div>

            <div class="columns">
                <div class="imageCont" v-if="data.page?.cover">
                    <div class="filler"></div>
                    <Image :image="data.page?.cover" :height="800" :width="800" :mobileHeight="824" imageClass="cover" />
                </div>
                <div class="priceCont">
                    <div class="headerText baron">price</div>
                    <p>€ {{ hardcopyPrice }} (plus shipping via regular mail)</p>
                    <p>€ {{ electronicCopyPrice }} (1 read-only licence, password-protected pdf file, not editable, not printable)</p>
                </div>
                <div class="detailsCont">
                    <div class="headerText baron">details</div>
                    <p>Year of publication: {{ data.page.publication_date.substring(0, 4) }}</p>
                    <p>ISBN: {{ data.page.isbn }}</p>
                    <p>{{ data.page.binding }}</p>
                </div>
                <div class="buttonCont">
                    <a href="mailto:info@eic-federation.eu">
                        <NButton size="large" type="primary">Order</NButton>
                    </a>
                </div>
                <div class="bodyCont">
                    <BlockBody :body="data?.page.body" />
                </div>
            </div>
        </article>
        <WebsiteFooter />
    </main>
</template>

<script lang="ts" setup>
import { NButton } from 'naive-ui'

const { getBook } = usePublications();
const directusUrl = useDirectusUrl()
const route = useRoute()
const { data } = useAsyncData(`book-${route.params.slug}`, () => getBook(route.params.slug));

const articleDate = computed(() => {
    if (data.value?.page && data.value.page.publication_date) return new Date(data.value.page.publication_date)
    else return null
})

const hardcopyPrice = computed(() => {
    if (data.value && data.value.page && data.value.page.price_hardcopy) {
        return parseFloat(data.value.page.price_hardcopy).toFixed(2).replace('00', '-')
    } else return null
})
const electronicCopyPrice = computed(() => {
    if (data.value && data.value.page && data.value.page.price_electronic_copy) {
        return parseFloat(data.value.page.price_electronic_copy).toFixed(2).replace('00', '-')
    } else return null
})

useSeoMeta({
    title: () => `${data.value?.page?.title} | Bookshop | Publications | EIC Federation`,
    ogTitle: () => `${data.value?.page?.title} | Bookshop |  Publications | EIC Federation`,
    twitterTitle: () => `${data.value?.page?.title} | Bookshop |  Publications | EIC Federation`,

    description: () => data.value?.page?.summary,
    ogDescription: () => data.value?.page?.summary,
    twitterDescription: () => data.value?.page?.summary,

    ogImage: () => data.value?.page?.cover?.id ? directusUrl + '/assets/' + data.value?.page.cover.id : undefined,
})
</script>

<style lang="scss" scoped>
main {
    article {
        padding: var(--size-padding-header) 0 var(--size-gutter-sections) 0;
        margin: 0 var(--size-margin-page);

        .infoBar {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--size-margin-page);

            margin-bottom: var(--size-gutter-default);
            color: var(--color-primary);

            .date {
                display: flex;
                gap: var(--size-gutter-default);
            }
        }

        h1 {
            margin-bottom: var(--size-gutter-double);
        }

        .columns {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--size-gutter-double) var(--size-margin-page);

            .imageCont {
                position: relative;
                width: 100%;
                grid-column: 2;
                background-color: #B3D8DF;
                max-width: 800px;
                //max-width: var(--text-maxwidth-body);

                .filler {
                    padding-top: 100%;
                }
                &:deep(.responsivePicture) {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 100%;
                }
            }

            .bodyCont,
            .buttonCont {
                grid-column: 2;
            }

            .priceCont,
            .detailsCont {
                grid-column: 2;
            }
        }
    }
}

@media screen and (max-width: 1280px) {
    main article {
        .infoBar {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }

        .columns {
            display: flex;
            flex-direction: column;
            gap: var(--size-gutter-double);

            .imageCont {
                max-width: var(--text-maxwidth-body);
            }
        }
    }
}
</style>